import React, { useState } from "react";
import { useReveal } from "../hooks/useReveal";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { CONTACT_EMAIL } from "../mock";

const empty = { name: "", email: "", company: "", message: "" };

export default function ContactSection({
  eyebrow = "Get in touch",
  heading = "Let's start a",
  emphasis = "conversation.",
  blurb = "Wholesale buyers, cafés and partners — tell us what you need and our trade desk will be in touch."
}) {
  const [ref, visible] = useReveal();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries/contact", { ...form, company: form.company || null });
      setReference(data.reference);
      setForm(empty);
      toast.success("Thank you — your enquiry has been sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#1f4a33]/25 py-3 text-[#10271a] placeholder:text-[#5e6b61]/60 focus:outline-none focus:border-[#1f4a33] transition-colors";

  return (
    <section id="contact" className="bg-[#1f4a33] text-[#f6f1e7] relative overflow-hidden grain">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 reveal ${visible ? "in-view" : ""}`}>
          <div>
            <p className="text-[#b08a5f] text-xs tracking-[0.25em] uppercase mb-6">{eyebrow}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light leading-[1.02]">
              {heading} <em className="italic text-[#e9d9bf]">{emphasis}</em>
            </h2>
            <p className="mt-7 text-[#f6f1e7]/75 text-lg leading-relaxed max-w-md">{blurb}</p>

            <div className="mt-12 text-[#f6f1e7]/85">
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-4 group" data-testid="contact-email-link">
                <span className="h-10 w-10 rounded-full bg-[#f6f1e7]/10 flex items-center justify-center group-hover:bg-[#f6f1e7]/20 transition-colors"><Mail size={18} /></span>
                <span className="break-all">{CONTACT_EMAIL}</span>
              </a>
              <p className="mt-4 text-sm text-[#f6f1e7]/55 ml-14">James Holden · Trade Desk</p>
            </div>
          </div>

          <form onSubmit={submit} className="bg-[#f6f1e7] rounded-3xl p-8 md:p-10 text-[#10271a]" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">Name</label>
                <input className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">Email</label>
                <input className={inputCls} type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" data-testid="contact-email" />
              </div>
            </div>
            <div className="mt-6">
              <label className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">Company <span className="normal-case tracking-normal">(optional)</span></label>
              <input className={inputCls} value={form.company} onChange={update("company")} placeholder="Company / café" data-testid="contact-company" />
            </div>
            <div className="mt-6">
              <label className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">Message</label>
              <textarea className={`${inputCls} resize-none`} rows={4} value={form.message} onChange={update("message")} placeholder="Tell us what you're looking for..." data-testid="contact-message" />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="mt-8 group inline-flex items-center gap-3 bg-[#1f4a33] text-[#f6f1e7] px-7 py-4 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors disabled:opacity-60"
            >
              {busy ? "Sending..." : "Send enquiry"}
              {!busy && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
            </button>
            {reference ? (
              <p className="mt-4 text-sm text-[#1f4a33]" data-testid="contact-reference">Sent — your reference is <span className="font-semibold">{reference}</span>. A copy has been emailed to you.</p>
            ) : (
              <p className="mt-4 text-xs text-[#5e6b61]">Your enquiry goes straight to our trade desk at {CONTACT_EMAIL}.</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
